"use client";

import { ModalType } from "@/components/Socials/Insta/Modal/Modal";
import { RecipeType, UserType } from "@/types/baseTypes";
import { getShoppingList } from "@/utils/serviceAPI/shoppinglist";
import {
  verifyUser,
  fetchUser,
  logoutUser,
  getFavorite,
} from "@/utils/serviceAPI/user";
import { useRouter } from "next/compat/router";
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";

export type FormModalType =
  | "text"
  | "reset"
  | "delete"
  | "recipe-tipp"
  | "recipe-filter"
  | "recipe-tools";

export interface IContextType {
  openMenu: boolean;
  setOpenMenu: Dispatch<SetStateAction<boolean>>;
  openPopup: boolean;
  setopenPopup: Dispatch<SetStateAction<boolean>>;
  instagramModal: ModalType | null;
  setInstagramModal: Dispatch<SetStateAction<ModalType | null>>;

  user: UserType | null;
  setUser: Dispatch<SetStateAction<UserType | null>>;

  formModalText: string;
  setFormModalText: Dispatch<SetStateAction<string>>;

  formModalType: FormModalType;
  setFormModalType: Dispatch<SetStateAction<FormModalType>>;

  logOut: () => Promise<void>;

  showSearch: boolean;
  setShowSearch: Dispatch<SetStateAction<boolean>>;

  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;

  recipeFav: { rezepte_list: RecipeType[]; rezepte_count: number } | undefined;
  setRecipeFav: Dispatch<
    SetStateAction<
      { rezepte_list: RecipeType[]; rezepte_count: number } | undefined
    >
  >;
  setFavoriteRecipes: () => Promise<void>;

  shoppingListRecipes: RecipeType[] | undefined;
  setShoppingList: () => Promise<void>;
}

const AppContext = createContext<IContextType | null>(null);

export const AppProvider = ({ children }: { children: React.ReactNode }) => {
  const [openMenu, setOpenMenu] = useState<boolean>(false);
  const [openPopup, setopenPopup] = useState<boolean>(false);
  const [instagramModal, setInstagramModal] = useState<ModalType | null>(null);
  const router = useRouter();
  const [user, setUser] = useState<UserType | null>(null);
  const [recipeFav, setRecipeFav] = useState<{
    rezepte_list: RecipeType[];
    rezepte_count: number;
  }>();
  const [formModalText, setFormModalText] = useState<string>("");
  const [formModalType, setFormModalType] = useState<FormModalType>("text");
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [shoppingListRecipes, setShoppingListRecipes] =
    useState<RecipeType[]>();
  async function logOut() {
    const res = await logoutUser();
    if (res && router) {
      setUser(null);
      // send user to login, if user logs out on profile page else reload
      if (router.asPath.includes("/profile")) {
        router.push("/login");
      } else router.reload();
      // DELETE recipe tracking list when session ends
      sessionStorage.removeItem("tracklist");
      sessionStorage.removeItem("userData");
      sessionStorage.removeItem("loggedIn");
    }
  }
  useEffect(() => {
    const setUserData = async () => {
      try {
        // skip validation for dev environment
        if (process.env.NODE_ENV === "development") {
          setUser({
            firstName: "dev",
            lastName: "dev",
            email: "dev@dev.de",
            locale: "de",
            isVerified: true,
            id: 1,
          });
          return;
        }
        if (
          sessionStorage.getItem("loggedIn") != null &&
          sessionStorage.getItem("loggedIn") !== "true"
        )
          return;
        const verifyRes = await verifyUser();

        const isLoggedIn = verifyRes?.data?.id != null;
        sessionStorage.setItem("loggedIn", isLoggedIn ? "true" : "false");

        const userData = JSON.parse(
          sessionStorage.getItem("userData") ?? "null",
        );

        if (isLoggedIn && userData != null) {
          setUser(userData);
          return;
        }

        const res = await fetchUser();
        if (res?.data) {
          setUser(res.data);
          sessionStorage.setItem("userData", JSON.stringify(res.data));
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
      }
    };
    setUserData();
  }, []);

  const fetchFavoriteRecipes = async (recipes: number[]) => {
    setLoading(true);
    const res = await fetch(
      `/api/recipes?type=recipesList&recipeIds=${recipes.join(",")}`,
    ).catch((e) => {
      // TODO handle error
      console.error(e);
    });
    if (!res || !res.ok) {
      console.error("Failed to fetch favorite recipes");
      setLoading(false);
      return;
    }
    const data = await res.json();
    if (data) {
      setRecipeFav(data);
    }
    setLoading(false);
  };
  const setFavoriteRecipes = async () => {
    // TODO
    if (user?.id) {
      const res = await getFavorite(user.id);
      const IdArray = res?.map((recipe) => recipe.recipe);
      if (IdArray?.length > 0) {
        fetchFavoriteRecipes(IdArray);
      } else {
        // FIXME not sure if this is the place to do this:
        setRecipeFav({ rezepte_list: [], rezepte_count: 0 });
      }
    }
  };

  /* jscpd:ignore-start */
  const fetchShoppingList = async (recipes: number[]) => {
    setLoading(true);
    const res = await fetch(
      `/api/recipes?type=shoppingList&recipeList=${recipes.join(",")}`,
    ).catch((e) => {
      // TODO handle error
      console.error(e);
    });
    if (!res || !res.ok) {
      console.error("Failed to fetch shopping list recipes");
      setLoading(false);
      return;
    }
    const data = await res.json();
    if (data) {
      setShoppingListRecipes(data);
    }
    setLoading(false);
  };
  /* jscpd:ignore-end */
  const setShoppingList = async () => {
    if (user?.id) {
      const res = await getShoppingList();
      const IdArray = res && res.length && res.map((recipe) => recipe.recipe);
      if (IdArray && IdArray?.length > 0) {
        fetchShoppingList(IdArray);
      }
    }
  };

  useEffect(() => {
    // ? Do only on first load or if no user data is set?
    setShoppingList();
    setFavoriteRecipes();
    // setAccountImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const handleRouteChange = () => {
      setOpenMenu(false);
    };
    // Subscribe to the route change event
    router?.events.on("routeChangeStart", handleRouteChange);

    // Cleanup the event listener when the component is unmounted
    return () => {
      router?.events.off("routeChangeStart", handleRouteChange);
    };
  }, [router?.events]);

  return (
    <AppContext.Provider
      value={{
        openMenu,
        setOpenMenu,
        openPopup,
        setopenPopup,
        instagramModal,
        setInstagramModal,
        user,
        setUser,
        formModalText,
        setFormModalText,
        formModalType,
        setFormModalType,
        logOut,
        setShowSearch,
        showSearch,
        loading,
        setLoading,
        recipeFav,
        setRecipeFav,
        setFavoriteRecipes,
        shoppingListRecipes,
        setShoppingList,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = (): IContextType => {
  const context = useContext<IContextType | null>(AppContext);

  if (context === undefined || context === null) {
    throw new Error("Context must be used within a Provider");
  }
  return context;
};
